import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import useWindowSize from "../../utils/useWindowSize";

const HotelVideos = () => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(null);
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const videoRef = useRef(null);
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const { width } = useWindowSize();
  const isMobile = width <= 500;
  const isTablet = width <= 600;

  const { hotel_videos: videos } = useSelector((state) => state.hotelInfo); //videos coming from redux only no extra state.

  const getThumbnailSize = () => {
    if (isTablet) {
      return {
        outer: 70,
        inner: 62,
      };
    }
    return {
      outer: 95,
      inner: 84,
    };
  };

  const thumbnailSize = getThumbnailSize();

  const resetVideo = (videoElement) => {
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
      videoElement.muted = true;
    }
  };

  const handleVideoAudio = () => {
    // Reset all videos first
    resetVideo(leftVideoRef?.current);
    resetVideo(rightVideoRef?.current);
    resetVideo(videoRef?.current);

    // Then set up and play the current video
    if (videoRef?.current && currentStoryIndex !== null) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch((error) => {
        console.log("Video autoplay failed:", error);
      });
    }
  };

  // Handle story index change and also plays any video that is clicked
  useEffect(() => {
    if (currentStoryIndex !== null) {
      document.body.style.overflow = "hidden";
      // Small timeout to ensure DOM is updated
      setTimeout(() => {
        handleVideoAudio();
      }, 0);
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, [currentStoryIndex]);

  const closeStory = () => {
    resetVideo(videoRef?.current);
    resetVideo(leftVideoRef?.current);
    resetVideo(rightVideoRef?.current);
    setCurrentStoryIndex(null);
    setProgress(0);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentStoryIndex < videos?.length - 1) {
        resetVideo(videoRef?.current);
        setProgress(0);
        setCurrentStoryIndex((prev) => prev + 1);
      } else if (diff < 0 && currentStoryIndex > 0) {
        resetVideo(videoRef?.current);
        setProgress(0);
        setCurrentStoryIndex((prev) => prev - 1);
      }
    }
    setTouchStart(null);
  };

  // Handle video progress
  useEffect(() => {
    const currentVideo = videoRef?.current;

    if (currentVideo) {
      const handleTimeUpdate = () => {
        if (!isNaN(currentVideo.duration) && currentVideo.duration > 0) {
          const percentage =
            (currentVideo.currentTime / currentVideo.duration) * 100;
          setProgress(percentage);
        }
      };

      currentVideo.addEventListener("timeupdate", handleTimeUpdate);

      return () => {
        currentVideo.removeEventListener("timeupdate", handleTimeUpdate);
        resetVideo(currentVideo);
      };
    }
  }, [currentStoryIndex]);

  // This will be triggered wher any video ends
  useEffect(() => {
    const currentVideo = videoRef?.current;

    if (currentVideo) {
      const handleVideoEnd = () => {
        if (currentStoryIndex < videos?.length - 1) {
          resetVideo(currentVideo);
          setProgress(0);
          setCurrentStoryIndex((prev) => prev + 1);
        } else {
          closeStory();
        }
      };

      currentVideo.addEventListener("ended", handleVideoEnd);

      // Reset video when component unmounts or video changes/cleaning operation
      return () => {
        currentVideo.removeEventListener("ended", handleVideoEnd);
        resetVideo(currentVideo);
      };
    }
  }, [currentStoryIndex, videos?.length]);

  const handleVideoClick = (newIndex) => {
    if (
      !isMobile &&
      newIndex >= 0 &&
      newIndex < videos?.length &&
      newIndex !== currentStoryIndex
    ) {
      resetVideo(videoRef?.current);
      setProgress(0);
      setCurrentStoryIndex(newIndex);
    }
  };

  // Force reset when video ref changes
  useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.currentTime = 0;
    }
  }, [currentStoryIndex]);

  const getVideoStyle = (index) => {
    if (isMobile) {
      return {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "100vw",
        height: "100dvh",
        objectFit: "contain",
        display: index === currentStoryIndex ? "block" : "none",
        zIndex: 30,
      };
    }

    const baseStyle = {
      width: "500px",
      objectFit: "contain",
      borderRadius: "10px",
      position: "absolute",
      top: "50%",
      transform: "translate(-50%, -50%)",
      transition: "left 0.5s ease, height 0.5s ease, transform 0.5s ease",
      display: "block",
      willChange: "transform, left, height",
      backgroundColor: "black",
      aspectRatio: "21/9",
      overflow: "hidden",
    };
    if (index === currentStoryIndex) {
      return {
        ...baseStyle,
        height: "65dvh",
        left: "50%",
        zIndex: 30,
      };
    } else if (index === currentStoryIndex - 1) {
      return {
        ...baseStyle,
        height: "45dvh",
        width: "380px",
        left: "30%",
        zIndex: 20,
        filter: "brightness(0.7)",
        cursor: "pointer",
      };
    } else if (index === currentStoryIndex + 1) {
      return {
        ...baseStyle,
        height: "45dvh",
        width: "380px",
        left: "70%",
        zIndex: 20,
        filter: "brightness(0.7)",
        cursor: "pointer",
      };
    }
    return {
      ...baseStyle,
      display: "none",
    };
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          gap: isTablet ? "8px" : "10px",
          overflow: "scroll",
          width: width < 900 ? "100%" : "50%",
          padding: isTablet ? "8px" : "10px",
        }}
      >
        {[...videos]?.map((video, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              // height: "80px",
            }}
          >
            <div
              onClick={() => setCurrentStoryIndex(index)}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: `${thumbnailSize?.outer}px`,
                height: `${thumbnailSize?.outer}px`,
                borderRadius: "50%",
                padding: isTablet ? "2px" : "3px",
                border: "2px solid var(--blue)",
                boxShadow: "0 0 4px #110011",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: `${thumbnailSize?.inner}px`,
                  height: `${thumbnailSize?.inner}px`,
                  borderRadius: "50%",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <video
                  preload="none"
                  playsInline
                  muted
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "auto",
                    height: "auto",
                    minWidth: "100%",
                    minHeight: "100%",
                    maxWidth: "150%",
                    maxHeight: "150%",
                    objectFit: "cover",
                  }}
                  poster={video?.thumbnail || "thumbnail"}
                >
                  <source src={video?.cdnURL} type="video/mp4" />
                </video>
              </div>
            </div>
            <p
              style={{
                fontFamily: "var(--volte)",
                fontSize: "11px",
                // marginTop: "4px",
                minHeight: "15px",
              }}
            >
              {video?.title?.length > 0 && video?.title}
            </p>
          </div>
        ))}
      </div>

      {currentStoryIndex !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={isMobile ? null : closeStory}
          // onClick={closeStory}
        >
          <button
            onClick={closeStory}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "rgba(255, 255, 255, 0.8)",
              border: "none",
              borderRadius: "50%",
              cursor: "pointer",
              width: "40px",
              height: "40px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "20px",
              zIndex: 10000,
            }}
          >
            ✖
          </button>

          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            {videos?.map((video, index) => {
              const style = getVideoStyle(index);
              if (!style) return null;

              const isActive = index === currentStoryIndex;
              const isLeft = index === currentStoryIndex - 1;
              const isRight = index === currentStoryIndex + 1;

              return (
                <div
                  key={index}
                  style={{
                    ...style,
                    aspectRatio: "16/9",
                    overflow: "hidden",
                  }}
                  onClick={(e) => {
                    if (!isMobile) {
                      e.stopPropagation();
                      handleVideoClick(index);
                    }
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "3px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      zIndex: 31,
                      overflow: "hidden",
                    }}
                  >
                    {isActive && (
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: "white",
                          transition: "width 0.1s linear",
                        }}
                      />
                    )}
                  </div>
                  <video
                    key={`video-${index}-${currentStoryIndex}`}
                    ref={
                      isActive
                        ? videoRef
                        : isLeft
                        ? leftVideoRef
                        : isRight
                        ? rightVideoRef
                        : null
                    }
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: isMobile ? "0" : "10px",
                      backgroundColor: "black",
                    }}
                    playsInline
                    preload="none"
                    muted={!isActive}
                    poster={video?.thumbnail || "thumbnail"}
                  >
                    <source src={video?.cdnURL} type="video/mp4" />
                  </video>
                  <div
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      zIndex: 31,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        minWidth: "50px",
                        minHeight: "50px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        position: "relative",
                        border: "2px solid var(--blue)",
                      }}
                    >
                      <video
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "auto",
                          height: "auto",
                          minWidth: "100%",
                          minHeight: "100%",
                          maxWidth: "150%",
                          maxHeight: "150%",
                          objectFit: "cover",
                        }}
                        playsInline
                        muted
                        poster={video?.thumbnail || "thumbnail"}
                      >
                        <source src={video?.cdnURL} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>{" "}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "bold",
                        fontFamily: "var(--volte)",
                        color: "white",
                      }}
                    >
                      {video?.title?.length > 0 && video?.title}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelVideos;
